import React, { FunctionComponent, useContext } from 'react'

import { Separator as FormRowSeparator, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { Field, Formik, FormikBag } from 'formikV2'
import { compose } from 'recompose'

import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { ActionType, PageName } from 'generated/types'
import { IErrorModalProps, withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError'

import { FormValues, formRows, initialValues } from './form'
import updateInvestorQuestionsMutation from './gql/updateInvestorQuestions.mutation.gql'
import { FormRow, HeroText, StyledFormContainer } from './styled-components'
import SubmitButton from './SubmitButton'
import { ToggleSwitch } from './ToggleSwitch'

type Props = WithFlowRouterProps & IErrorModalProps

export const enhance = compose<Props, {}>(withApolloErrorModal, withFlowRouter)

const InvestorQuestions = ({ closeErrorModal, route, showErrorModal }: Props) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { isLoadingRemoteStrings, getRemoteString } = useRemoteStrings()
  const [updateInvestorQuestions] = useMutation(updateInvestorQuestionsMutation)

  const handleFormSubmit = async (values: FormValues, formikBag: FormikBag<Props, FormValues>) => {
    formikBag.setSubmitting(true)

    try {
      await updateInvestorQuestions({ variables: values })
      isDynamicFunnelEnabled
        ? routeToPage(PageName.InvestorQuestions, ActionType.Continue)
        : route({ from: FlowRoute.InvestorQuestions, action: 'continue' })
    } catch (error) {
      const retryHandler = () => {
        closeErrorModal()
        formikBag.submitForm()
      }
      const { title, message, actions, image } = getGeneralError(
        error,
        { closeErrorModal },
        retryHandler,
      )
      showErrorModal({ title, message, actions, image })
      sendToSentry(error)
    }
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.InvestorQuestions)} />
      <Layout>
        {isLoadingRemoteStrings ? (
          <Spinner />
        ) : (
          <>
            <HeroText>{getRemoteString({ key: 'investor_questions.hero_text' })}</HeroText>
            <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
              <>
                <StyledFormContainer>
                  {formRows.map(row => (
                    <div key={row.name}>
                      <FormRow>
                        <Field
                          name={row.name}
                          component={ToggleSwitch}
                          label={getRemoteString({ key: row.labelKey })}
                        />
                      </FormRow>
                      <FormRowSeparator />
                    </div>
                  ))}
                </StyledFormContainer>
                <Separator />
                <CTAContainer>
                  <SubmitButton />
                </CTAContainer>
              </>
            </Formik>
          </>
        )}
      </Layout>
    </>
  )
}

export default enhance(InvestorQuestions as FunctionComponent)
